import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch } from './api';

// Per rendere la card "1. Seleziona Destinatari" separata, assicurati di renderizzare questo componente fuori dal body della card "Iniziative in corso" nel componente padre.
function ContattiPrenotazionePartecipanti({ idManager, onSelectionChange }) {
  const [partecipanti, setPartecipanti] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  // Filtri
  const [filterNome, setFilterNome] = useState('');
  const [filterCognome, setFilterCognome] = useState('');
  const [filterNomeCognome, setFilterNomeCognome] = useState('');
  const [filterMail, setFilterMail] = useState('');
  const [filterEnte, setFilterEnte] = useState('');
  const [filterHaDetermina, setFilterHaDetermina] = useState('');
  const [filterOnline, setFilterOnline] = useState('');
  const [filterStato, setFilterStato] = useState('');
  // Ordinamento
  const [sortField, setSortField] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const selectAllRef = useRef();

  const stableOnSelectionChange = useCallback((data) => {
    if (onSelectionChange) {
      onSelectionChange(data);
    }
  }, []);

  useEffect(() => {
    async function fetchPartecipanti() {
      if (!idManager) {
        setPartecipanti([]);
        setLoading(false);
        return;
      }
      
      console.log('=== DEBUG FETCH PARTECIPANTI ===');
      console.log('idManager:', idManager);
      
      // DEBUG TOKEN
      const token = localStorage.getItem('token');
      console.log('Token presente:', !!token);
      console.log('Token length:', token?.length || 0);
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          console.log('Token payload:', payload);
          console.log('Token exp:', payload.exp);
          console.log('Token exp date:', new Date(payload.exp * 1000));
          console.log('Token scaduto:', Date.now() >= payload.exp * 1000);
        } catch (e) {
          console.log('Errore parsing token:', e);
        }
      }
      
      setLoading(true);
      try {
        console.log('Chiamando:', `/api/Iniziative/prenotazioni/${idManager}`);
        const response = await apiFetch(`/api/Iniziative/prenotazioni/${idManager}`);
        
        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
        
        if (response.ok) {
          const data = await response.json();
          console.log('Data ricevuta:', data);
          setPartecipanti(Array.isArray(data) ? data : []);
        } else {
          console.log('Response NON OK');
          const errorText = await response.text();
          console.log('Error response:', errorText);
          
          // Se è 401 Unauthorized, il token è scaduto
          if (response.status === 401) {
            console.log('⚠️ TOKEN SCADUTO - Redirect al login');
            localStorage.removeItem('token');
            window.location.href = '/';
            return;
          }
          
          setPartecipanti([]);
        }
      } catch (error) {
        console.error('Fetch error:', error);
        setPartecipanti([]);
      }
      setLoading(false);
    }
    
    fetchPartecipanti();
  }, [idManager]);

  // Separato useEffect per pulire le selezioni quando cambia l'iniziativa
  useEffect(() => {
    setSelected({});
    stableOnSelectionChange({
      selectedRows: [],
      destinatariCount: 0,
      emailCount: 0,
      placeholders: []
    });
  }, [idManager, stableOnSelectionChange]);

  // Filtri
  useEffect(() => {
    let f = Array.isArray(partecipanti) ? partecipanti : [];
    
    // 🔧 RICERCA UNIFICATA Nome/Cognome (ricerca in entrambi i campi)
    if (filterNomeCognome.trim()) {
      const searchTerm = filterNomeCognome.toLowerCase();
      f = f.filter(p => 
        (p.nome || '').toLowerCase().includes(searchTerm) || 
        (p.cognome || '').toLowerCase().includes(searchTerm)
      );
    }
    
    if (filterMail) f = f.filter(p => (p.mail || '').toLowerCase().includes(filterMail.toLowerCase()));
    if (filterEnte) f = f.filter(p => (p.ente || '').toLowerCase().includes(filterEnte.toLowerCase()));
    if (filterHaDetermina) {
      if (filterHaDetermina === 'SI') f = f.filter(p => p.haDetermina === true);
      if (filterHaDetermina === 'NO') f = f.filter(p => p.haDetermina === false);
    }
    if (filterStato) {
      const stato = filterStato.toLowerCase();
      if (stato === 'in regola') f = f.filter(p => (p.stato || '').toLowerCase().includes('in regola'));
      else if (stato === 'moroso più anni') f = f.filter(p => {
        const s = (p.stato || '').toLowerCase();
        return s.includes('moroso') && s.includes('anni');
      });
      else if (stato === 'moroso') f = f.filter(p => {
        const s = (p.stato || '').toLowerCase();
        return s.includes('moroso') && !s.includes('anni');
      });
      else if (stato === 'non associato') f = f.filter(p => (p.stato || '').toLowerCase().includes('non associato'));
    }
    // Filtro Online
    if (filterOnline) {
      if (filterOnline === 'SI') f = f.filter(p => p.riserva === true);
      if (filterOnline === 'NO') f = f.filter(p => p.riserva === false);
    }
    
    // Ordinamento stabile
    if (sortField) {
      f = [...f].sort((a, b) => {
        let va = a[sortField], vb = b[sortField];
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va === vb) return 0;
        if (va === undefined || va === null) return 1;
        if (vb === undefined || vb === null) return -1;
        return (va > vb ? 1 : -1) * (sortAsc ? 1 : -1);
      });
    }
    setFiltered(f);
  }, [filterNomeCognome, filterMail, filterEnte, filterHaDetermina, filterOnline, filterStato, partecipanti, sortField, sortAsc]); // 🔧 AGGIORNATE dipendenze

  // Selezione multipla su singola riga (chiave unica per ogni riga)
  const getRowKey = (p, idx) => `${p.idPrenotazione}-${idx}`;
  const handleSelect = (rowKey, checked) => {
    if (!idManager) return; // Non permettere selezioni senza iniziativa selezionata
    
    console.log('handleSelect called:', { rowKey, checked, idManager });
    
    setSelected(prev => {
      const updated = { ...prev, [rowKey]: checked };
      console.log('Updated selection:', updated);
      
      // Chiamiamo la callback dopo l'aggiornamento dello stato
      setTimeout(() => {
        const selRows = filtered.filter((p, idx) => updated[getRowKey(p, idx)]);
        console.log('Selected rows:', selRows);
        
        // Mappiamo i partecipanti per avere la struttura corretta per le email
        const mappedRows = selRows.map(p => ({
          ...p,
          emailOrd: p.mail ? [p.mail] : [], // Il campo mail diventa emailOrd array
          emailPec: [] // I partecipanti di solito non hanno PEC
        }));
        
        // Genera i segnaposto con prefisso PRENOTAZIONE_
        const sample = mappedRows[0] || {};
        const fieldPlaceholders = Object.keys(sample)
          .filter(k => !['emailOrd', 'emailPec', 'idPrenotazione'].includes(k))
          .map(k => `{PRENOTAZIONE_${k.toUpperCase()}}`);
        const genericPlaceholders = ['{DATA_ODIERNA}', '{IMMAGINE_INTESTAZIONE}', '{IMMAGINE_FONDO}', '{FIRMA_ANUTEL}'];
        const placeholders = Array.from(new Set([...fieldPlaceholders, ...genericPlaceholders]));
        
        const callbackData = {
          selectedRows: mappedRows,
          destinatariCount: mappedRows.length,
          emailCount: mappedRows.reduce((sum, p) => sum + (p.emailOrd?.length || 0), 0),
          placeholders: placeholders
        };
        
        console.log('Calling onSelectionChange with:', callbackData);
        stableOnSelectionChange(callbackData);
      }, 0);
      
      return updated;
    });
  };

  const handleSelectAll = (checked) => {
    if (!idManager) return; // Non permettere selezioni senza iniziativa selezionata
    
    if (checked) {
      const newSel = {};
      filtered.forEach((p, idx) => { newSel[getRowKey(p, idx)] = true; });
      setSelected(newSel);
      
      // Chiamiamo la callback dopo l'aggiornamento dello stato
      setTimeout(() => {
        // Mappiamo i partecipanti per avere la struttura corretta per le email
        const mappedRows = filtered.map(p => ({
          ...p,
          emailOrd: p.mail ? [p.mail] : [], // Il campo mail diventa emailOrd array
          emailPec: [] // I partecipanti di solito non hanno PEC
        }));
        
        // Genera i segnaposto con prefisso PRENOTAZIONE_
        const sample = mappedRows[0] || {};
        const fieldPlaceholders = Object.keys(sample)
          .filter(k => !['emailOrd', 'emailPec', 'idPrenotazione'].includes(k))
          .map(k => `{PRENOTAZIONE_${k.toUpperCase()}}`);
        const genericPlaceholders = ['{DATA_ODIERNA}', '{IMMAGINE_INTESTAZIONE}', '{IMMAGINE_FONDO}', '{FIRMA_ANUTEL}'];
        const placeholders = Array.from(new Set([...fieldPlaceholders, ...genericPlaceholders]));
        
        stableOnSelectionChange({
          selectedRows: mappedRows,
          destinatariCount: mappedRows.length,
          emailCount: mappedRows.reduce((sum, p) => sum + (p.emailOrd?.length || 0), 0),
          placeholders: placeholders
        });
      }, 0);
    } else {
      setSelected({});
      stableOnSelectionChange({
        selectedRows: [],
        destinatariCount: 0,
        emailCount: 0,
        placeholders: []
      });
    }
  };

  // Aggiorna stato indeterminate del checkbox seleziona tutti
  const selectedRows = filtered.filter((p, idx) => selected[getRowKey(p, idx)]);
  const selectedCount = selectedRows.length;
  const totalCount = filtered.length;
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedCount > 0 && selectedCount < filtered.length;
    }
  }, [selected, filtered, selectedCount]);

  // Ordinamento handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  // Colonne visibili
  const columns = [
    { key: 'nome', label: 'Nome' },
    { key: 'cognome', label: 'Cognome' },
    { key: 'mail', label: 'Mail' },
    //{ key: 'qualifica', label: 'Qualifica', hide: true },
    //{ key: 'codiceFiscale', label: 'Codice Fiscale', hide: true },
    { key: 'telefono', label: 'Telefono' },
    { key: 'ente', label: 'Ente' },
    { key: 'riserva', label: 'Online' },
    { key: 'haDetermina', label: 'Ha Determina' },
    { key: 'stato', label: 'Stato' },
  ];

  // Card separata, tabella ordinabile, checkbox seleziona tutti, stato evidenziato, colonne nascoste
  return (
    <div className="card my-4">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0"><i className="fas fa-users"></i> 1. Seleziona Destinatari</h5>
          <div>
            <span className="badge bg-primary me-2"><i className="fas fa-list-ol me-1"></i>Totale: {totalCount}</span>
            <span className="badge bg-success">Selezionati: {selectedCount}</span>
          </div>
        </div>
      </div>
      <div className="card-body">
        {!idManager ? (
          <div className="text-center text-muted py-5">
            <i className="fas fa-info-circle fa-3x mb-3"></i>
            <h5>Seleziona prima un'iniziativa</h5>
            <p>Per visualizzare i partecipanti è necessario selezionare un'iniziativa dalla tabella sopra.</p>
          </div>
        ) : (
          <>
            {/* Filtri - UNIFORMATI CON ETICHETTE E 2 RIGHE */}
            <div className="row mb-2">
              <div className="col-md-4">
                <label className="form-label fw-bold text-start w-100">
                  <i className="fas fa-user me-1 text-muted"></i> Nome/Cognome:
                </label>
                <input 
                  type="text" 
                  className="form-control form-control-sm" 
                  placeholder="Cerca in nome o cognome..." 
                  value={filterNomeCognome} 
                  onChange={e => setFilterNomeCognome(e.target.value)} 
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold text-start w-100">
                  <i className="fas fa-envelope me-1 text-muted"></i> Email:
                </label>
                <input 
                  type="text" 
                  className="form-control form-control-sm" 
                  placeholder="Filtra per email..." 
                  value={filterMail} 
                  onChange={e => setFilterMail(e.target.value)} 
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold text-start w-100">
                  <i className="fas fa-university me-1 text-muted"></i> Ente:
                </label>
                <input 
                  type="text" 
                  className="form-control form-control-sm" 
                  placeholder="Filtra per ente..." 
                  value={filterEnte} 
                  onChange={e => setFilterEnte(e.target.value)} 
                />
              </div>
            </div>
            
            <div className="row mb-3">
              <div className="col-md-3">
                <label className="form-label fw-bold text-start w-100">
                  <i className="fas fa-file-contract me-1 text-muted"></i> Ha Determina:
                </label>
                <select 
                  className="form-select form-select-sm" 
                  value={filterHaDetermina} 
                  onChange={e => setFilterHaDetermina(e.target.value)}
                >
                  <option value="">Tutti</option>
                  <option value="SI">SI</option>
                  <option value="NO">NO</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label fw-bold text-start w-100">
                  <i className="fas fa-globe me-1 text-muted"></i> Online:
                </label>
                <select 
                  className="form-select form-select-sm" 
                  value={filterOnline} 
                  onChange={e => setFilterOnline(e.target.value)}
                >
                  <option value="">Tutti</option>
                  <option value="SI">SI</option>
                  <option value="NO">NO</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold text-start w-100">
                  <i className="fas fa-user-check me-1 text-muted"></i> Stato Associazione:
                </label>
                <select
                  className="form-select form-select-sm"
                  value={filterStato}
                  onChange={e => setFilterStato(e.target.value)}
                >
                  <option value="">Tutti gli stati</option>
                  <option value="in regola">Associato in regola</option>
                  <option value="moroso">Associato moroso</option>
                  <option value="moroso più anni">Insolvente</option>
                  <option value="non associato">Non associato</option>
                </select>
              </div>
            </div>

            {/* Tabella */}
            <div className="table-responsive position-relative" style={{ maxHeight: 400, overflowY: 'auto' }}>
              {loading && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white bg-opacity-75" style={{zIndex:10}}>
                  <i className="fas fa-spinner fa-spin fa-3x text-primary mb-3"></i>
                  <div className="fw-bold fs-5">Caricamento in corso...</div>
                </div>
              )}
              <table className="table table-striped table-hover mb-0">
                <thead className="table-dark sticky-top">
                  <tr>
                    <th style={{width:'40px'}}>
                      <input type="checkbox" ref={selectAllRef} checked={selectedCount === filtered.length && filtered.length > 0} onChange={e => handleSelectAll(e.target.checked)} />
                    </th>
                    {columns.map(col => (
                      <th key={col.key} style={{cursor:'pointer'}} onClick={() => handleSort(col.key)}>
                        {col.label} {sortField === col.key ? (sortAsc ? <i className="fas fa-sort-up"></i> : <i className="fas fa-sort-down"></i>) : <i className="fas fa-sort text-secondary"></i>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length > 0 ? filtered.map((p, idx) => {
                    // Stato cell bg
                    let statoClass = '';
                    const statoLower = (p.stato || '').toLowerCase();
                    if (statoLower.includes('in regola')) statoClass = 'bg-success text-white';
                    else if (statoLower.includes('moroso') && statoLower.includes('anni')) statoClass = 'bg-danger text-white';
                    else if (statoLower.includes('moroso')) statoClass = 'bg-warning';
                    else if (statoLower.includes('non associato')) statoClass = '';
                    const rowKey = getRowKey(p, idx);
                    return (
                      <tr key={rowKey}>
                        <td><input type="checkbox" checked={!!selected[rowKey]} onChange={e => handleSelect(rowKey, e.target.checked)} /></td>
                        <td>{p.nome}</td>
                        <td>{p.cognome}</td>
                        <td>{p.mail}</td>
                        <td>{p.telefono}</td>
                        <td>{p.ente}</td>
                        <td><span className={`badge ${p.riserva ? 'bg-primary' : 'bg-secondary'}`}>{p.riserva ? 'SI' : 'NO'}</span></td>
                        <td><span className={`badge ${p.haDetermina ? 'bg-success' : 'bg-secondary'}`}>{p.haDetermina ? 'SI' : 'NO'}</span></td>
                        <td className={statoClass}>{p.stato}</td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={columns.length+1} className="text-center text-muted py-4">
                        <i className="fas fa-address-book fa-2x mb-2"></i><br />Nessun partecipante trovato
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ContattiPrenotazionePartecipanti;
